// Analyse des rapports de prospection envoyés par les commerciaux sur WhatsApp.
//
// Depuis janvier 2024, les commerciaux rendent compte de leurs tournées dans un
// groupe WhatsApp, dans une forme remarquablement constante :
//
//   « Forcalquier 17/06 285 flyers 4 argus »
//   « Le 17 septembre 300 F à St tulle »
//   « Le 18/06 Niozelles 90flyers la brillanne 194 flyers 5 argus »
//
// Soit : une DATE, une COMMUNE, un NOMBRE DE FLYERS, et souvent des « argus »
// (contacts qualifiés), des apporteurs d'affaires et des rendez-vous.
//
// Ce module transforme cet historique en données exploitables. Il ne devine
// JAMAIS : un rapport dont la commune est introuvable ou ambiguë est rendu avec
// la mention « à vérifier » plutôt qu'attribué au hasard. Une carte fausse est
// pire qu'une carte incomplète.
//
// Fonctions PURES : ni réseau ni base, donc testables ligne à ligne.

/** Un passage de prospection, tel qu'on peut le reconstituer d'un message. */
export interface PassageProspection {
  /** AAAA-MM-JJ */
  date: string;
  /** Nom de l'auteur tel qu'il apparaît dans WhatsApp. */
  auteur: string;
  /** Nom de commune trouvé dans le texte, non encore rapproché du référentiel. */
  communeBrute: string | null;
  flyers: number;
  argus: number;
  apporteurs: number;
  rdv: number;
  /** Message d'origine, conservé pour que tout chiffre reste vérifiable. */
  message: string;
  /** 'sure' : lecture non ambiguë. 'a_verifier' : à confirmer à la main. */
  fiabilite: 'sure' | 'a_verifier';
  /** Pourquoi le passage est à vérifier. */
  raison: string | null;
}

export interface AnalyseProspection {
  passages: PassageProspection[];
  /** Messages du groupe ne contenant aucun rapport de tournée. */
  horsSujet: number;
  /** Messages reconstitués au total (lignes de suite recollées). */
  messages: number;
}

const MOIS = [
  'janvier',
  'fevrier',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'aout',
  'septembre',
  'octobre',
  'novembre',
  'decembre',
];

/** Minuscules sans accents ni ponctuation : la forme sur laquelle on compare. */
export function normaliser(s: string): string {
  const sansAccents = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return (
    sansAccents
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      // Sépare lettres et chiffres collés : ils écrivent « 90flyers » et
      // « la brillanne194 flyers », où le nom resterait sinon introuvable.
      .replace(/([a-z])(\d)/g, '$1 $2')
      .replace(/(\d)([a-z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Mots de liaison retirés avant de comparer un nom de commune.
 *
 * Le référentiel officiel écrit « Saint-Michel-l'Observatoire » là où le
 * commercial tape « saint Michel observatoire ». Comparer mot pour mot
 * échouerait sur la moitié des villages du secteur.
 */
const LIAISONS = new Set([
  'l',
  'le',
  'la',
  'les',
  'd',
  'de',
  'du',
  'des',
  'sur',
  'en',
  'au',
  'aux',
]);

/** Forme comparable d'un nom de lieu : normalisée, sans mots de liaison. */
export function cleLieu(s: string): string {
  return normaliser(s)
    .split(' ')
    .filter((mot) => mot !== '' && !LIAISONS.has(mot))
    .join(' ');
}

/** En-tête WhatsApp : [JJ/MM/AAAA HH:MM:SS] Auteur: message */
const ENTETE = /^‎?\[(\d{2})\/(\d{2})\/(\d{4}) \d{2}:\d{2}:\d{2}\]\s([^:]+):\s?(.*)$/;

interface MessageBrut {
  date: string;
  auteur: string;
  texte: string;
}

/**
 * Recolle les messages d'un export WhatsApp.
 *
 * Un message sur plusieurs lignes n'a d'en-tête que sur la première : les
 * suivantes appartiennent au même message. Les traiter séparément couperait
 * « Lundi, Villeneuve 386 flyer » du rapport auquel il se rattache.
 */
export function lireExportWhatsApp(contenu: string): MessageBrut[] {
  const messages: MessageBrut[] = [];
  for (const ligne of contenu.split('\n')) {
    const m = ENTETE.exec(ligne.replace(/\r$/, ''));
    if (m?.[1] && m[2] && m[3] && m[4]) {
      messages.push({
        date: `${m[3]}-${m[2]}-${m[1]}`,
        auteur: m[4].trim(),
        texte: m[5] ?? '',
      });
    } else {
      const dernier = messages[messages.length - 1];
      if (dernier) dernier.texte += ` ${ligne.trim()}`;
    }
  }
  return messages;
}

/** Nombre suivi de « flyers », « flyer » ou « F ». Tolère l'absence d'espace. */
const FLYERS = /(\d{1,4})\s*(?:flyers?|f)\b/gi;
const ARGUS = /(\d{1,3})\s*argus/gi;
const APPORTEURS = /(?:(\d{1,2})\s*)?apporteurs?\s*d/gi;
const RDV = /(?:(\d{1,2})\s*)?(?:rdv|rendez[\s-]?vous)/gi;

/** Somme des occurrences d'un motif ; les mentions sans nombre comptent pour 1. */
function compter(texte: string, motif: RegExp): number {
  let total = 0;
  for (const m of texte.matchAll(motif)) total += m[1] ? Number(m[1]) : 1;
  return total;
}

/** Jours d'écart entre deux dates AAAA-MM-JJ. */
function ecartJours(a: string, b: string): number {
  return Math.abs(Date.parse(`${a}T12:00:00Z`) - Date.parse(`${b}T12:00:00Z`)) / 86_400_000;
}

/**
 * Date à retenir pour un rapport.
 *
 * Le commercial écrit souvent la date dans son message (« Le 18/06 », « 17
 * mars ») parce qu'il rend compte le lendemain. Cette date-là prime — mais
 * seulement si elle tombe près de l'envoi : au-delà, c'est une faute de frappe
 * ou un autre nombre pris pour une date, et l'horodatage du message vaut mieux.
 */
export function dateDuRapport(texte: string, dateMessage: string): string {
  const annee = Number(dateMessage.slice(0, 4));
  const candidats: string[] = [];

  for (const m of texte.matchAll(/\b(\d{1,2})\s*[/.]\s*(\d{1,2})\b/g)) {
    const j = Number(m[1]);
    const mo = Number(m[2]);
    if (j >= 1 && j <= 31 && mo >= 1 && mo <= 12) {
      candidats.push(
        `${String(annee)}-${String(mo).padStart(2, '0')}-${String(j).padStart(2, '0')}`,
      );
    }
  }
  const enLettres = new RegExp(`\\b(\\d{1,2})\\s+(${MOIS.join('|')})`, 'gi');
  for (const m of normaliser(texte).matchAll(enLettres)) {
    const j = Number(m[1]);
    const mo = MOIS.indexOf(m[2] ?? '') + 1;
    if (j >= 1 && j <= 31 && mo > 0) {
      candidats.push(
        `${String(annee)}-${String(mo).padStart(2, '0')}-${String(j).padStart(2, '0')}`,
      );
    }
  }

  // La date écrite doit rester proche de l'envoi. 45 jours laissent passer un
  // rapport tardif ou un message de fin d'année, et écartent les aberrations.
  const proches = candidats.filter((c) => ecartJours(c, dateMessage) <= 45);
  return proches[0] ?? dateMessage;
}

/**
 * Cherche les communes du référentiel citées dans un texte, dans l'ordre où
 * elles apparaissent.
 *
 * Le rapprochement est EXACT sur la forme normalisée : « St tulle » ne
 * ressemble pas assez à « Sainte-Tulle » pour qu'on l'accepte sans preuve, mais
 * le référentiel contient les deux graphies, fournies par l'appelant.
 */
export function communesCitees(texte: string, referentiel: string[]): string[] {
  const t = ` ${cleLieu(texte)} `;
  const trouvees: { nom: string; position: number; longueur: number }[] = [];
  // Les noms les plus longs d'abord : « saint michel observatoire » doit gagner
  // sur « saint michel », sinon on rattache au mauvais village.
  for (const nom of [...referentiel].sort((a, b) => cleLieu(b).length - cleLieu(a).length)) {
    const n = cleLieu(nom);
    if (n === '') continue;
    const position = t.indexOf(` ${n} `);
    if (position === -1) continue;
    // Une commune déjà couverte par un nom plus long n'est pas une seconde.
    if (trouvees.some((d) => position >= d.position && position < d.position + d.longueur + 2)) {
      continue;
    }
    trouvees.push({ nom: n, position, longueur: n.length });
  }
  return trouvees.sort((a, b) => a.position - b.position).map((d) => d.nom);
}

/**
 * Analyse un export WhatsApp complet.
 *
 * `referentiel` est la liste des noms de communes connus — le module ne va
 * jamais les chercher lui-même : c'est à l'appelant de fournir un référentiel
 * officiel, et de lui rattacher ensuite les codes INSEE et les coordonnées.
 */
export function analyserProspection(contenu: string, referentiel: string[]): AnalyseProspection {
  const messages = lireExportWhatsApp(contenu);
  const passages: PassageProspection[] = [];
  let horsSujet = 0;

  for (const msg of messages) {
    const nombres = [...msg.texte.matchAll(FLYERS)].map((m) => Number(m[1]));
    if (nombres.length === 0) {
      horsSujet += 1;
      continue;
    }

    const date = dateDuRapport(msg.texte, msg.date);
    const communes = communesCitees(msg.texte, referentiel);
    const argus = compter(msg.texte, ARGUS);
    const apporteurs = compter(msg.texte, APPORTEURS);
    const rdv = compter(msg.texte, RDV);

    const base = {
      date,
      auteur: msg.auteur,
      message: msg.texte.trim(),
      argus,
      apporteurs,
      rdv,
    };

    if (communes.length === 0) {
      // Sans lieu, le passage n'a pas sa place sur la carte : on le garde tout
      // de même, pour que les totaux restent justes et qu'on puisse le situer
      // à la main.
      passages.push({
        ...base,
        communeBrute: null,
        flyers: nombres.reduce((t, n) => t + n, 0),
        fiabilite: 'a_verifier',
        raison: 'aucune commune reconnue dans le message',
      });
      continue;
    }

    if (communes.length === 1) {
      passages.push({
        ...base,
        communeBrute: communes[0] ?? null,
        flyers: nombres.reduce((t, n) => t + n, 0),
        fiabilite: 'sure',
        raison: null,
      });
      continue;
    }

    // Plusieurs communes. Autant de communes que de nombres : on apparie dans
    // l'ordre du texte, ce qui correspond à la façon dont ils écrivent
    // (« Niozelles 90 flyers la brillanne 194 flyers »).
    if (communes.length === nombres.length) {
      communes.forEach((commune, i) => {
        passages.push({
          ...base,
          // Les compteurs annexes portent sur le message entier : les rattacher
          // au premier passage seulement éviterait de les compter deux fois.
          argus: i === 0 ? argus : 0,
          apporteurs: i === 0 ? apporteurs : 0,
          rdv: i === 0 ? rdv : 0,
          communeBrute: commune,
          flyers: nombres[i] ?? 0,
          fiabilite: 'sure',
          raison: null,
        });
      });
      continue;
    }

    // Déséquilibre : on ne répartit pas au jugé. Un seul passage, à vérifier.
    passages.push({
      ...base,
      communeBrute: communes[0] ?? null,
      flyers: nombres.reduce((t, n) => t + n, 0),
      fiabilite: 'a_verifier',
      raison: `${String(communes.length)} communes citées pour ${String(nombres.length)} nombre(s) de flyers`,
    });
  }

  return { passages, horsSujet, messages: messages.length };
}
